import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { Culture } from 'src/app/models/culture';
import { Group } from 'src/app/models/group';
import { Role } from 'src/app/models/role';
import { User } from 'src/app/models/user';
import { CultureService } from 'src/app/services/culture.service';
import { GroupsService } from 'src/app/services/groups.service';
import { RolesService } from 'src/app/services/roles.service';
import { GroupTreeItemComponent } from './group-tree-item/group-tree-item.component';

/**
 * Composant utilisé pour afficher les utilisateurs et groupes utilitaires sous la forme d'un arbre hierarchique.
 */
@Component({
    selector: 'app-groups-tree',
    templateUrl: './groups-tree.component.html',
    styleUrls: ['./groups-tree.component.scss'],
    imports: [GroupTreeItemComponent]
})
export class GroupsTreeComponent implements OnInit {
    /**
     * Liste des groupes racines.
     */
    public roots: Group[];

    /**
     * Liste de l'ensemble des rôles.
     * Utilisé pour afficher le libellé du rôle d'un groupe ou d'un utilisateur.
     */
    public roles: Role[];

    /**
     * Liste de l'ensemble des langues.
     * Utilisé pour afficher le libellé de la langue d'un utilisateur.
     */
    public cultures: Culture[];

    /**
     * Indique si les utilisateurs doivent être affichés (true) ou si seuls les groupes d'utilisateurs doivent être affichés (false).
     */
    @Input() isDisplayUser: boolean;

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'un groupe.
     */
    @Output()
    groupEdited: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'un groupe.
     */
    @Output()
    groupDisplayed: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Réinitialiser' d'un groupe.
     */
    @Output()
    groupReseted: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'un utilisateur.
     */
    @Output()
    userEdited: EventEmitter<User> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'un utilisateur.
     */
    @Output()
    userDisplayed: EventEmitter<User> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Réinitialiser' d'un utilisateur.
     */
    @Output()
    userReseted: EventEmitter<User> = new EventEmitter();

    /**
     * Références aux composants correspondant aux noeuds groupes utilisateurs de l'arbre.
     */
    @ViewChildren(GroupTreeItemComponent) treeChildren: QueryList<GroupTreeItemComponent>;

    /**
     * Constructeur.
     * @param groupsService Service pour gérer les groupes.
     * @param rolesService Service pour gérer les rôles.
     * @param cultureService Service pour gérer les langues.
     */
    constructor(
        public groupsService: GroupsService,
        public rolesService: RolesService,
        public cultureService: CultureService
    ) {}

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        // Récupération des groupes racines.
        this.groupsService.roots().subscribe((x) => {
            this.roots = x;
        });

        // Récupération des rôles.
        this.rolesService.ListAttributable().subscribe((x) => {
            this.roles = x.value;
        });

        // Récupération des langues.
        this.cultureService.List().subscribe((x) => {
            this.cultures = x;
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel d'un groupe.
     * @param group Group sélectionné.
     */
    public onGroupDisplayed(group: Group) {
        this.groupDisplayed.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel d'un groupe.
     * @param group Group sélectionné.
     */
    public onGroupEdited(group: Group) {
        this.groupEdited.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Réinitialialiser' dans le menu contextuel d'un groupe.
     * @param group Group sélectionné.
     */
    public onGroupReseted(group: Group) {
        this.groupReseted.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel d'un utilisateur.
     * @param user User sélectionné.
     */
    public onUserDisplayed(user: User) {
        this.userDisplayed.emit(user);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel d'un utilisateur.
     * @param user User sélectionné.
     */
    public onUserEdited(user: User) {
        this.userEdited.emit(user);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Réinitialiser' dans le menu contextuel d'un utilisateur.
     * @param user User sélectionné.
     */
    public onUserReseted(user: User) {
        this.userReseted.emit(user);
    }

    /**
     * Raffraichit l'affichage d'un groupe.
     * @param groupId : Identifiant du groupe.
     */
    public refreshGroup(groupId: string) {
        if (this.roots.some((x) => x.id === groupId)) {
            // Récupération des groupes racines.
            this.groupsService.roots().subscribe((x) => {
                this.roots = x;
            });
        }

        this.treeChildren.toArray().forEach((item, index) => {
            item.refreshGroup(groupId);
        });
    }

    /**
     * Raffraichit l'affichage d'un utilisateur.
     * @param userId : Identifiant de l'utilisateur.
     */
    public refreshUser(userId: string) {
        this.treeChildren.toArray().forEach((item, index) => {
            item.refreshUser(userId);
        });
    }
}
